import { useMutation, UseMutationOptions, useQueries, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { realEstateDescriptionFields } from '../../common/forms/generalRealEstate/fields'
import i18n from '../../i18n'
import { mapValuesToSelectOptions } from '../../utils/formUtils/forms'
import { ClonePropertyParams, ClonePropertyResponse, DeleteMultipleResponse, PageListParams } from '../globalTypes'
import { invalidateQueries, updateToast } from '../helpers'
import {
    cloneProperty,
    createUpdateProperty,
    deleteProperties,
    deletePropertyById,
    fetchPropertyValues,
    getOpportunityById,
    getProperties,
    getPropertyById,
} from './functions'
import {
    OpportunityActivity,
    OpportunityContact,
    OpportunityListParams,
    PropertiesDeleteParams,
    Property,
    PropertyCriteria,
    PropertyData,
    PropertyDataList,
    PropertyDeleteParams,
    PropertyMutationResponse,
    PropertyParams,
    PropertyPayload,
    PropertyValuesData,
} from './types'

const properties = {
    GetProperty: {
        useQuery: (id: string, params?: PropertyParams, options?: Omit<UseQueryOptions<PropertyData, AxiosError>, 'queryKey' | 'queryFn'>) =>
            useQuery({
                queryKey: params ? ['properties', id, params] : ['properties', id],
                queryFn: () => getPropertyById(id, params),
                staleTime: 1 * 60 * 1000,
                ...options,
            }),
    },
    GetPropertiesById: {
        useQueries: (ids: number[]) =>
            useQueries({
                queries: ids.map((id) => ({
                    queryKey: ['properties', id.toString()],
                    queryFn: () => getPropertyById(id.toString()),
                    staleTime: 1 * 60 * 1000,
                    enabled: !!id,
                })),
            }),
    },
    GetProperties: {
        useQuery: (
            params: PageListParams<PropertyCriteria>,
            options?: Omit<UseQueryOptions<PropertyDataList, AxiosError>, 'queryKey' | 'queryFn'>
        ) => {
            return useQuery({
                queryKey: ['properties', 'list', params],
                queryFn: () => getProperties(params),
                staleTime: 1 * 60 * 1000,
                ...options,
                enabled: options?.enabled ?? true,
            })
        },
    },
    GetOpportunity: {
        useQuery: (params: OpportunityListParams, options?: UseQueryOptions<OpportunityActivity | OpportunityContact, AxiosError>) =>
            useQuery({
                queryKey: ['properties', 'list', 'opportunity', params],
                queryFn: () => getOpportunityById(params),
                refetchOnWindowFocus: 'always', // refetch if user switches between 2 tabs
                enabled: Boolean(params?.page && params?.limit && params?.order),
                ...options,
            }),
    },
    CreateUpdateProperty: {
        useMutation: (options?: UseMutationOptions<PropertyMutationResponse, AxiosError, PropertyPayload>) => {
            const history = useHistory()

            return useMutation({
                onMutate: () => {
                    const toastId = toast.loading(i18n.t('toast.loading') as string)
                    return toastId
                },
                mutationFn: (payload: PropertyPayload) => createUpdateProperty(payload),
                onSuccess: (data, variables, context) => {
                    const { isEdit, setFieldValue } = variables

                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.success') as string, 'success')

                    invalidateQueries(['properties', data.id.toString()])
                    invalidateQueries(['properties', 'list'])
                    invalidateQueries(['notifications', 'deliveries'])

                    // reset PropertyFormDescriptionAI fields
                    setFieldValue?.(`description.${realEstateDescriptionFields.PROPERTY_DESCRIPTION_EMPHASIS}`, null)
                    setFieldValue?.(`description.${realEstateDescriptionFields.PROPERTY_DESCRIPTION_LANG}`, null)

                    !isEdit && history.push(`/properties/${data.id}/edit`)
                },
                onError: (error, variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.error') as string, 'error')
                },
                ...options,
            })
        },
    },
    DeleteProperty: {
        useMutation: (options?: UseMutationOptions<Property, AxiosError, PropertyDeleteParams>) => {
            return useMutation({
                onMutate: (params: PropertyDeleteParams) => {
                    toast.loading(i18n.t('toast.loading.delete') as string, { toastId: params.id })
                },
                mutationFn: (params: PropertyDeleteParams) => deletePropertyById(params.id),
                onSuccess: (data, params) => {
                    const { id } = params ?? {}
                    updateToast(id, i18n.t('toast.update.delete.success') as string, 'success')

                    invalidateQueries(['properties', 'list'])
                    invalidateQueries(['notifications', 'deliveries', 'unread-count'])
                },
                onError: (error, params) => {
                    updateToast(params.id, i18n.t('toast.update.delete.error') as string, 'error')
                    console.error(error)
                },
                ...options,
            })
        },
    },
    DeleteProperties: {
        useMutation: (options?: UseMutationOptions<DeleteMultipleResponse, AxiosError, PropertiesDeleteParams>) => {
            return useMutation({
                onMutate: (params: PropertiesDeleteParams) => {
                    const { ids } = params
                    toast.loading(i18n.t('toast.loading.delete.properties.number', { requestsToBeDeletedCount: ids.length }) as string, {
                        toastId: ids[0],
                    })
                },
                mutationFn: (params: PropertiesDeleteParams) => deleteProperties(params.ids),
                onSuccess: (data, params) => {
                    const { ids } = params ?? {}

                    const failedCount = data?.notFoundIds?.length
                    const successCount = data?.successfulDelete?.length
                    const requestsToBeDeletedCount = ids?.length

                    const successMessage = i18n.t('toast.loading.delete.properties.number.success', {
                        successCount,
                        requestsToBeDeletedCount,
                    }) as string
                    const partialErrorMessage = i18n.t('toast.loading.delete.properties.number.error', {
                        failedCount,
                        requestsToBeDeletedCount,
                    }) as string

                    if (failedCount > 0) {
                        updateToast(ids?.[0], partialErrorMessage, 'error')
                    }

                    if (successCount > 0) {
                        updateToast(ids?.[0], successMessage, 'success')
                        invalidateQueries(['properties', 'list'])
                        invalidateQueries(['notifications', 'deliveries', 'unread-count'])
                    }
                },
                onError: (error, params) => {
                    const { ids } = params ?? {}
                    updateToast(ids?.[0], i18n.t('toast.update.delete.error'), 'error')
                    console.error(error)
                },
                ...options,
            })
        },
    },
    CloneProperty: {
        useMutation: (options?: UseMutationOptions<ClonePropertyResponse, AxiosError, ClonePropertyParams>) => {
            const history = useHistory()

            return useMutation({
                onMutate: () => {
                    const toastId = toast.loading(i18n.t('toast.loading') as string)
                    return toastId
                },
                mutationFn: (params) => cloneProperty(params.id, params.data),
                onSuccess: (data, _, context) => {
                    const { id } = data
                    history.push(`/properties/${id}/edit`)

                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.success') as string, 'success')
                    invalidateQueries(['properties', 'list'])
                },
                onError: (error, _, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.error') as string, 'error')

                    console.log('Clone property error:', error)
                },
                ...options,
            })
        },
    },
    FormValues: {
        useQuery: (
            fields?: string[],
            formFilters?: Record<string, string | string[]>,
            options?: Omit<UseQueryOptions<PropertyValuesData, AxiosError>, 'queryKey' | 'queryFn'>
        ) => {
            const query = useQuery({
                queryKey: ['propertyValues', fields, formFilters],
                queryFn: () => fetchPropertyValues(fields, formFilters),
                staleTime: Infinity,
                gcTime: Infinity,
                ...options,
            })

            const formattedData = useMemo(() => {
                return mapValuesToSelectOptions(query.data)
            }, [query.data])

            return {
                ...query,
                data: formattedData,
            }
        },
    },
}

export default properties
